import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Deployment } from 'types/deployment';

import { addErrorNotification } from 'services/notification';
import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useBillingMutation } from 'hooks/api/useBillingMutation';
import { useMutationWithError } from 'hooks/api/useMutationWithError';

export function useDeployment(
  id: string | null | undefined,
  { enabled, ...options }: UseQueryOptions<Deployment, APIError> = {}
) {
  const { applicationID, client } = useAPI();

  return useAuthenticatedQuery(
    ['deployment', id, applicationID],
    async () => {
      if (!id) {
        return Promise.reject('No deployment id provided.');
      }

      const { data } = await client.get(
        `/apps/${applicationID}/deployments/${id}`
      );

      return data;
    },
    {
      ...options,
      enabled: enabled !== undefined ? Boolean(id) && enabled : Boolean(id),
    }
  );
}

type ToggleState = 'stopping' | 'deploying';
type ToggleOptions = UseMutationOptions<Deployment, APIError, Deployment, any>;

export function useToggleDeploymentState(options: ToggleOptions = {}) {
  const { applicationID, client } = useAPI();

  const { mutate: start, isLoading: isStarting } = useBillingMutation(
    async (deployment: Deployment) => {
      const { data } = await client.post(
        `/apps/${applicationID}/deployments/${deployment.id}/start`
      );

      return data;
    },
    {
      ...options,
      entity: 'deployment',
      action: 'start',
      onError(error) {
        addErrorNotification({ title: 'Failed to start deployment.', error });
      },
    }
  );
  const { mutate: stop, isLoading: isStopping } = useMutationWithError(
    async (deployment: Deployment) => {
      const { data } = await client.post(
        `/apps/${applicationID}/deployments/${deployment.id}/stop`
      );

      return data;
    },
    {
      ...options,
      entity: 'deployment',
      action: 'stop',
      onError(error) {
        addErrorNotification({ title: 'Failed to stop deployment.', error });
      },
    }
  );

  function toggleState(deployment: Deployment): ToggleState {
    const isActive =
      deployment.state === 'deploying' ||
      deployment.state === 'running' ||
      deployment.state === 'restart_waiting';

    if (isActive) {
      stop(deployment);
      return 'stopping';
    }

    start(deployment);
    return 'deploying';
  }

  return {
    toggleState,
    isLoading: isStarting || isStopping,
  };
}
